import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isIOS } from 'react-device-detect';

import { joinClassNames } from '../../../util/util';
import { PLAYER_TRANSITIONS_OUT } from '../../../util/constants';
import { useConfiguration } from '../../../contexts/ConfigurationContext';

import './TransitionLayer.scss';

interface TransitionLayerProps {
	isMobile?: boolean;
}

const TransitionLayer = ({ isMobile: _isMobile }: TransitionLayerProps) => {
	const {
		showCountdownModule,
		currentMediaId,
		isTransitionLayerEnable,
		transitionType,
		isTransitionEffectActive,
		mediasData,
		isMediaLive,
		setTransitionLayer,
		setTransitionEffectStatus,
		configParams,
	} = useConfiguration();

	const wrapperRef = useRef<HTMLDivElement | null>(null);
	const canvasRef = useRef<HTMLCanvasElement | null>(null);

	// iOS: blob URL stored in state so JSX background-image updates declaratively
	const [iosBlobUrl, setIosBlobUrl] = useState('');
	const offscreenCanvasRef = useRef<HTMLCanvasElement | null>(null);
	const prevBlobUrlRef = useRef(''); // tracks previous URL so it can be revoked

	// Tracks the outgoing scene's media ID — initialized immediately so the
	// first transition can look up the right player before any effect fires
	const outgoingMediaIdRef = useRef(currentMediaId);

	// Only update the outgoing ID while not transitioning, so capture functions
	// always look up the scene that was playing when the user clicked
	useEffect(() => {
		if (!isTransitionLayerEnable) {
			outgoingMediaIdRef.current = currentMediaId;
		}
	}, [currentMediaId, isTransitionLayerEnable]);

	// iOS: pre-capture frames via interval so any transition has a real blob URL ready.
	// Uses requestVideoFrameCallback when available (iOS 15.4+) since drawImage on
	// hardware-decoded HLS only works reliably inside that callback on iOS.
	// Falls back to plain drawImage for older iOS / non-HLS content.
	useEffect(() => {
		if (!isIOS) { return; }

		const storeBlob = (canvas: HTMLCanvasElement) => {
			canvas.toBlob((blob) => {
				if (!blob) { return; }
				const prev = prevBlobUrlRef.current;
				const url = URL.createObjectURL(blob);
				prevBlobUrlRef.current = url;
				setIosBlobUrl(url);
				if (prev) { URL.revokeObjectURL(prev); }
			}, 'image/jpeg', 0.8);
		};

		const capture = () => {
			const activePlayerId = `player-${outgoingMediaIdRef.current}-${configParams.containerKey}`;
			const widget = (window as any).GoBrain.widgets(activePlayerId);
			const video = widget?.element.querySelector('video.vjs-tech') as HTMLVideoElement | null;
			if (!video || !video.videoWidth || !video.videoHeight || video.currentTime <= 0) { return; }

			offscreenCanvasRef.current ??= document.createElement('canvas');
			const canvas = offscreenCanvasRef.current;
			const w = video.videoWidth;
			const h = video.videoHeight;
			if (canvas.width !== w) { canvas.width = w; }
			if (canvas.height !== h) { canvas.height = h; }

			if ('requestVideoFrameCallback' in video) {
				// Inside requestVideoFrameCallback the frame is guaranteed CPU-readable
				(video as any).requestVideoFrameCallback(() => {
					try {
						canvas.getContext('2d')?.drawImage(video, 0, 0, w, h);
						storeBlob(canvas);
					} catch (_) { /* canvas taint */ }
				});
			} else {
				// Fallback: plain drawImage — may produce black on iOS HLS
				try {
					const ctx = canvas.getContext('2d');
					ctx?.drawImage(video, 0, 0, w, h);
					try {
						const pixel = ctx?.getImageData(w >> 1, h >> 1, 1, 1);
						if (!pixel || !(pixel.data[0] > 5 || pixel.data[1] > 5 || pixel.data[2] > 5)) { return; }
					} catch (_) { /* cross-origin taint — content drawn, proceed */ }
					storeBlob(canvas);
				} catch (_) { /* tainted canvas */ }
			}
		};

		const interval = setInterval(capture, 2000);
		return () => {
			clearInterval(interval);
			if (prevBlobUrlRef.current) { URL.revokeObjectURL(prevBlobUrlRef.current); }
		};
	}, [configParams.containerKey]);

	// Non-iOS: capture directly from the video element into the visible canvas.
	const captureToCanvas = useCallback(() => {
		const canvas = canvasRef.current;
		if (!canvas) { return; }

		canvas.style.backgroundColor = '';

		const activePlayerId = `player-${outgoingMediaIdRef.current}-${configParams.containerKey}`;
		const widget = (window as any).GoBrain.widgets(activePlayerId);
		const video = widget?.element.querySelector('video.vjs-tech') as HTMLVideoElement | null;

		if (!video) { return; }

		const ctx = canvas.getContext('2d');
		const w = video.videoWidth || window.innerWidth;
		const h = video.videoHeight || window.innerHeight;

		if (canvas.width !== w) { canvas.width = w; }
		if (canvas.height !== h) { canvas.height = h; }

		try {
			ctx?.drawImage(video, 0, 0, w, h);
		} catch (_) {
			return;
		}

		try {
			const pixel = ctx?.getImageData(w >> 1, h >> 1, 1, 1);
			if (pixel && pixel.data[3] > 10) {
				canvas.style.backgroundColor = '#000';
			}
		} catch (_) {
			canvas.style.backgroundColor = '#000';
		}
	}, [configParams.containerKey]);

	// useLayoutEffect runs before the browser paints so the frame is visible
	// the instant the transition layer appears
	useLayoutEffect(() => {
		const wrapper = wrapperRef.current;
		if (!wrapper) { return; }

		if (isTransitionLayerEnable) {
			if (isIOS) {
				// Force instant appearance — inline styles win over CSS class transitions.
				wrapper.style.transition = 'none';
				wrapper.style.opacity = '1';
			} else {
				captureToCanvas();
			}
		} else {
			if (isIOS) {
				wrapper.style.transition = '';
				wrapper.style.opacity = '';
			}
		}
	}, [isTransitionLayerEnable, captureToCanvas]);

	const currentMedia = mediasData.find((media: any) => media?.id === currentMediaId);

	useEffect(() => {
		if (currentMedia?.live && !currentMedia?.start && !currentMedia?.end && currentMedia?.duration === 0) {
			setTransitionLayer(false);
			setTransitionEffectStatus(false);
		}

		if (!isTransitionEffectActive) {
			return;
		}

		const delay = !isIOS && transitionType === PLAYER_TRANSITIONS_OUT.fadeOut ? 600 : 0;
		const timer = setTimeout(() => {
			setTransitionLayer(false);
			setTransitionEffectStatus(false);
		}, delay);

		return () => clearTimeout(timer);
	}, [transitionType, isTransitionEffectActive, mediasData, currentMediaId, isTransitionLayerEnable, isMediaLive, currentMedia, setTransitionLayer, setTransitionEffectStatus]);

	return (
		<div
			ref={wrapperRef}
			style={isIOS && iosBlobUrl ? { backgroundImage: `url(${iosBlobUrl})` } : undefined}
			className={joinClassNames(
				'transition-wrapper',
				isTransitionLayerEnable && !showCountdownModule ? 'enabled' : '',
				isTransitionEffectActive ? `${transitionType}-effect` : ''
			)}
		>
			{!isIOS && <canvas ref={canvasRef} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />}
		</div>
	);
};

export default TransitionLayer;
